import React, { useEffect, useMemo, useState } from "react";
import styled from "styled-components";
import { Container } from "react-bootstrap";
import { listProjects, listJuniors } from "../../lib/firebase";
import { projectCodenameSync } from "../../utils/codename";
import { SEED_MEMBERS, SEED_PROJECTS } from "./seed";
import BubbleMap from "./BubbleMap";
import EmptyMap from "./EmptyMap";
import MobileList from "./MobileList";

const SectionStyled = styled.section`
  padding: 128px 0 96px;
  background: var(--nd-black);
  min-height: 100vh;
  color: var(--nd-text-primary);
`;

const Kicker = styled.div`
  font-family: 'Space Mono', monospace;
  font-size: 11px;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  color: var(--nd-text-secondary);
  margin-bottom: 16px;
`;

const Title = styled.h1`
  font-family: 'Space Grotesk', sans-serif;
  font-size: 48px;
  font-weight: 500;
  color: var(--nd-text-display);
  letter-spacing: -0.02em;
  line-height: 1.05;
  margin: 0 0 16px;
  @media (max-width: 768px) { font-size: 32px; }
`;

const Lede = styled.p`
  font-family: 'Space Grotesk', sans-serif;
  font-size: 18px;
  font-weight: 300;
  color: var(--nd-text-secondary);
  line-height: 1.5;
  max-width: 640px;
  margin: 0 0 32px;
`;

const Divider = styled.hr`
  border: 0;
  border-top: 1px solid var(--nd-border);
  margin: 24px 0 24px;
`;

const LayerChips = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  margin-bottom: 16px;
`;

const LayerChip = styled.button`
  font-family: 'Space Mono', monospace;
  font-size: 10px;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  background: ${({ $active }) => ($active ? "var(--nd-text-display)" : "transparent")};
  color: ${({ $active }) => ($active ? "var(--nd-black)" : "var(--nd-text-disabled)")};
  border: 1px solid var(--nd-border-visible);
  border-radius: 999px;
  padding: 5px 12px;
  cursor: pointer;
  outline: none;
  &:hover { color: ${({ $active }) => ($active ? "var(--nd-black)" : "var(--nd-text-display)")}; }
`;

const LegendRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 18px;
  margin-bottom: 24px;
  font-family: 'Space Mono', monospace;
  font-size: 11px;
  letter-spacing: 0.06em;
  color: var(--nd-text-secondary);
`;

const LegendItem = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 8px;
`;

const Square = styled.span`
  display: inline-block;
  width: 12px;
  height: 12px;
  background: var(--nd-text-display);
  border-radius: 2px;
`;

const Dot = styled.span`
  display: inline-block;
  width: 10px;
  height: 10px;
  border-radius: 50%;
  background: var(--nd-text-display);
`;

const Ring = styled.span`
  display: inline-block;
  width: 12px;
  height: 12px;
  border-radius: 50%;
  border: 1.5px solid var(--nd-text-secondary);
`;

const Diamond = styled.span`
  display: inline-block;
  width: 10px;
  height: 10px;
  border: 1.5px solid var(--nd-accent-subtle);
  transform: rotate(45deg);
`;

const Desktop = styled.div`
  @media (max-width: 768px) { display: none; }
`;

const Mobile = styled.div`
  display: none;
  @media (max-width: 768px) { display: block; }
`;

const SeedBanner = styled.div`
  font-family: 'Space Mono', monospace;
  font-size: 10px;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: var(--nd-text-disabled);
  padding: 8px 12px;
  border: 1px dashed var(--nd-border-visible);
  border-radius: 4px;
  margin-bottom: 16px;
  display: inline-block;
`;

const StatsRow = styled.div`
  margin-top: 32px;
  padding-top: 24px;
  border-top: 1px solid var(--nd-border);
  font-family: 'Space Mono', monospace;
  font-size: 11px;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: var(--nd-text-disabled);
  display: flex;
  flex-wrap: wrap;
  gap: 24px;
`;

const StatChunk = styled.span`
  color: var(--nd-text-primary);
  & > b {
    color: var(--nd-text-display);
    font-weight: 700;
    margin-right: 6px;
  }
`;

const Legend = () => (
  <LegendRow>
    <LegendItem><Square /> dioscuro</LegendItem>
    <LegendItem><Dot /> team</LegendItem>
    <LegendItem><Ring /> project</LegendItem>
    <LegendItem><Diamond /> tech</LegendItem>
  </LegendRow>
);

const Stats = ({ members, projects, techs }) => (
  <StatsRow>
    <StatChunk><b>{members}</b> people</StatChunk>
    <StatChunk><b>{projects}</b> projects</StatChunk>
    <StatChunk><b>{techs}</b> techs</StatChunk>
  </StatsRow>
);

const LoadingState = styled.div`
  font-family: 'Space Mono', monospace;
  font-size: 11px;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: var(--nd-text-disabled);
  padding: 32px 0;
`;

const buildGraph = (members, projects) => {
  const activeP = projects.filter((p) => p.status === "active");
  const memberIdsInUse = new Set(activeP.flatMap((p) => p.memberIds || []));
  const visibleMembers = members.filter((m) => memberIdsInUse.has(m.id));
  // Unique tech LABELS for stats (set), but the tech NODES below are
  // duplicated per project so each one is a degree-1 leaf. Avoids
  // cross-cutting edges that crowd the outer ring.
  const uniqueTechLabels = Array.from(
    new Set(activeP.flatMap((p) => p.stack || [])),
  );

  const ORG = "org:dioscuro";

  // Per-project tech leaves. Same tech label can appear N times in the
  // graph (one node per project that uses it) — clarity over uniqueness.
  const techNodes = activeP.flatMap((p) =>
    (p.stack || []).map((t) => ({
      id: `t:${p.id}:${t}`,
      type: "tech",
      label: t,
      projectId: p.id,
    })),
  );

  const nodes = [
    { id: ORG, type: "org", label: "Dioscuro" },
    ...visibleMembers.map((m) => ({
      id: m.id,
      type: "member",
      label: m.name,
      role: m.role,
      seniority: m.seniority,
    })),
    ...activeP.map((p) => ({
      id: `p:${p.id}`,
      type: "project",
      label: projectCodenameSync(p.id),
      industry: p.industry || "other",
      status: p.status,
    })),
    ...techNodes,
  ];

  const links = [
    ...visibleMembers.map((m) => ({ source: ORG, target: m.id })),
    ...activeP.flatMap((p) =>
      (p.memberIds || []).map((mid) => ({ source: mid, target: `p:${p.id}` })),
    ),
    ...techNodes.map((n) => ({ source: `p:${n.projectId}`, target: n.id })),
  ];

  return {
    graph: { nodes, links },
    activeProjects: activeP,
    visibleMembers,
    uniqueTechs: uniqueTechLabels,
  };
};

// Lift Firestore juniors into the member shape so a newly assigned junior
// appears in the map without manual seed edits.
const juniorsToMembers = (juniors) =>
  juniors.filter((j) => !j.offboarded).map((j) => ({
    id: `m:junior:${j.id}`,
    name: j.name || "—",
    role: j.role || "Junior DevOps Engineer",
    seniority: "junior",
  }));

const liftJuniorProjects = (juniors, projects) => {
  const out = projects.map((p) => ({ ...p, memberIds: p.memberIds || [] }));
  juniors.forEach((j) => {
    if (!j.assignedProjectId) return;
    const idx = out.findIndex((p) => p.id === j.assignedProjectId);
    if (idx < 0) return;
    const memberId = `m:junior:${j.id}`;
    if (!out[idx].memberIds.includes(memberId)) {
      out[idx].memberIds = [...out[idx].memberIds, memberId];
    }
  });
  return out;
};

// Filter a built graph to a subset of node types. Org node always kept.
const filterGraph = (graph, visibleTypes) => {
  const keepId = new Set(
    graph.nodes
      .filter((n) => n.type === "org" || visibleTypes[n.type])
      .map((n) => n.id),
  );
  return {
    nodes: graph.nodes.filter((n) => keepId.has(n.id)),
    links: graph.links.filter((l) => keepId.has(l.source) && keepId.has(l.target)),
  };
};

const InfrastructureSection = () => {
  const [projects, setProjects] = useState([]);
  const [juniors, setJuniors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [visibleTypes, setVisibleTypes] = useState({
    member: true,
    project: true,
    tech: true,
  });
  const toggleType = (t) =>
    setVisibleTypes((v) => ({ ...v, [t]: !v[t] }));

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [allProjects, allJuniors] = await Promise.all([
          listProjects(),
          listJuniors(),
        ]);
        if (cancelled) return;
        setProjects(allProjects);
        setJuniors(allJuniors);
      } catch (err) {
        // eslint-disable-next-line no-console
        console.error("[infrastructure] load failed:", err);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const { graph, activeProjects, visibleMembers, uniqueTechs, seed } =
    useMemo(() => {
      const hasLive = projects.length > 0;
      if (!hasLive) {
        const built = buildGraph(SEED_MEMBERS, SEED_PROJECTS);
        return { ...built, seed: true };
      }
      const activeJuniors = juniors.filter((j) => !j.offboarded);
      const liveMembers = [...SEED_MEMBERS, ...juniorsToMembers(activeJuniors)];
      const liveProjects = liftJuniorProjects(activeJuniors, projects);
      const built = buildGraph(liveMembers, liveProjects);
      return { ...built, seed: false };
    }, [projects, juniors]);

  if (loading) {
    return (
      <SectionStyled>
        <Container>
          <Kicker>INFRASTRUCTURE</Kicker>
          <LoadingState>loading…</LoadingState>
        </Container>
      </SectionStyled>
    );
  }

  if (graph.nodes.length <= 1) {
    return (
      <SectionStyled>
        <Container>
          <Kicker>INFRASTRUCTURE</Kicker>
          <EmptyMap />
        </Container>
      </SectionStyled>
    );
  }

  return (
    <SectionStyled>
      <Container>
        <Kicker>INFRASTRUCTURE</Kicker>
        <Title>Live delivery map</Title>
        <Lede>
          Dioscuro to team to projects to tech. Project identifiers are
          obfuscated by design — codenames are deterministic but don't
          reveal client identity.
        </Lede>
        {seed && (
          <SeedBanner>· Seed data · populate /admin-projects to switch to live ·</SeedBanner>
        )}
        <Divider />
        <Legend />
        <LayerChips>
          <LayerChip
            type="button"
            $active={visibleTypes.member}
            aria-pressed={visibleTypes.member}
            onClick={() => toggleType("member")}
          >
            {visibleTypes.member ? "✓ " : ""}team
          </LayerChip>
          <LayerChip
            type="button"
            $active={visibleTypes.project}
            aria-pressed={visibleTypes.project}
            onClick={() => toggleType("project")}
          >
            {visibleTypes.project ? "✓ " : ""}projects
          </LayerChip>
          <LayerChip
            type="button"
            $active={visibleTypes.tech}
            aria-pressed={visibleTypes.tech}
            onClick={() => toggleType("tech")}
          >
            {visibleTypes.tech ? "✓ " : ""}tech
          </LayerChip>
        </LayerChips>
        <Desktop>
          <BubbleMap graph={filterGraph(graph, visibleTypes)} />
        </Desktop>
        <Mobile>
          <MobileList projects={activeProjects} members={visibleMembers} />
        </Mobile>
        <Stats
          members={visibleMembers.length}
          projects={activeProjects.length}
          techs={uniqueTechs.length}
        />
      </Container>
    </SectionStyled>
  );
};

export default InfrastructureSection;
